import { CloseCircleOutlined } from "@ant-design/icons"
import { Button, Flex, Typography } from "antd"
import React from "react"

import Icon from "../../../../../Icon"
import type { CalendarFilters } from "../../../redux/calendar.types.ts"
import { SEMANTIC_EVENT_COLORS } from "../../constants/event-colors.ts"
import { CalendarFiltersSearch } from "./CalendarFiltersSearch.tsx"
import { CalendarFiltersTeamMembers } from "./CalendarFiltersTeamMembers.tsx"

const { Text } = Typography

interface CalendarFiltersProps {
  filters: CalendarFilters
  searchValue?: string
  onSearchChange?: (value: string) => void
  onFilterChange: (filter: Record<string, unknown>) => void
  onSelectEvent: (id: string) => void
  onClearFilters?: () => void
}

const chipStyle = (key: keyof typeof SEMANTIC_EVENT_COLORS) => ({
  color: SEMANTIC_EVENT_COLORS[key].text_color,
  backgroundColor: SEMANTIC_EVENT_COLORS[key].background_color
})

const typeButtons = [
  {
    key: "EVENT",
    label: "Events",
    icon: <Icon type={"Calendar"} />,
    style: chipStyle("event")
  },
  {
    key: "REMINDER",
    label: "Reminders",
    icon: <Icon type={"Bell"} />,
    style: chipStyle("reminder")
  }
]

const entityButtons = [
  {
    key: "project",
    label: "Projects",
    icon: <Icon type={"Share"} />,
    style: chipStyle("project")
  },
  {
    key: "lead",
    label: "Leads",
    icon: <Icon type={"Handshake"} />,
    style: chipStyle("lead")
  }
]

export function CalendarFilters({
  filters,
  searchValue = "",
  onSearchChange = () => {},
  onFilterChange,
  onSelectEvent,
  onClearFilters = () => {}
}: CalendarFiltersProps) {
  const activeFilterCount =
    (searchValue.trim() ? 1 : 0) +
    (filters.type !== null ? 1 : 0) +
    (filters.entity_type !== null ? 1 : 0) +
    (filters.participants?.length ? 1 : 0)
  // type: null означає обидва активні
  const isTypeActive = (key: string) => {
    return filters.type === null || filters.type === key
  }

  // entity_type: null означає обидва активні
  const isEntityActive = (key: string) => {
    return filters.entity_type === null || filters.entity_type === key
  }

  const handleTypeToggle = (key: "EVENT" | "REMINDER") => {
    if (filters.type === null) {
      // Було обидва → вибираємо протилежний
      onFilterChange({ type: key === "EVENT" ? "REMINDER" : "EVENT" })
    } else if (filters.type === key) {
      // Вимикаємо поточний → вмикаємо обидва
      onFilterChange({ type: null })
    } else {
      // Вмикаємо інший → обидва активні
      onFilterChange({ type: null })
    }
  }

  const handleEntityToggle = (key: "lead" | "project") => {
    if (filters.entity_type === null) {
      onFilterChange({ entity_type: key === "lead" ? "project" : "lead" })
    } else if (filters.entity_type === key) {
      onFilterChange({ entity_type: null })
    } else {
      onFilterChange({ entity_type: null })
    }
  }

  const onFilterChangeHandler =
    (key: keyof CalendarFilters) => (value: unknown) => {
      onFilterChange({ [key]: value || null })
    }

  const renderFilterButton = (
    { key, label, icon, style }: (typeof typeButtons)[0],
    isActive: boolean,
    onClick: () => void
  ) => (
    <Button
      key={key}
      className={
        "px-3 h-9 rounded-md font-medium transition-colors duration-150 shadow-none"
      }
      onClick={onClick}
      style={
        isActive
          ? { ...style, borderColor: style.color }
          : {
              borderColor: "#E5E7EB",
              color: "#6B7280",
              backgroundColor: "transparent"
            }
      }
    >
      {icon} {label}
    </Button>
  )

  return (
    <Flex gap={"small"} wrap align={"center"}>
      <CalendarFiltersSearch
        value={searchValue}
        onChange={onSearchChange}
        onSelectEvent={onSelectEvent}
      />

      <Flex gap="small" wrap>
        {entityButtons.map((btn) =>
          renderFilterButton(btn, isEntityActive(btn.key), () =>
            handleEntityToggle(btn.key as "lead" | "project")
          )
        )}
        {typeButtons.map((btn) =>
          renderFilterButton(btn, isTypeActive(btn.key), () =>
            handleTypeToggle(btn.key as "EVENT" | "REMINDER")
          )
        )}
      </Flex>

      <CalendarFiltersTeamMembers
        value={filters?.["participants"]}
        onSelectParticipants={onFilterChangeHandler("participants")}
      />

      {activeFilterCount > 0 && (
        <Button
          type={"text"}
          size={"small"}
          onClick={onClearFilters}
          icon={<CloseCircleOutlined />}
          className={"text-gray-500"}
        >
          <Text className={"text-xs"}>Clear filters</Text>
        </Button>
      )}
    </Flex>
  )
}
